import usePageTitle from "../utils/usePageTitle";
import { ActivityPageContext } from "./dashboard/ActivityPageContext";
import { canBrowseTeamSessions } from "./dashboard/personalSessionsModel";
import * as Sentry from "@sentry/browser";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import {
  Navigate,
  Outlet,
  Route,
  type RouteObject,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { ToastFeed, add_toast_action, delete_old_toasts } from "common-tailwind-components";
import {
  type WsStateFunctional,
  cleanupWs,
  configure_drr,
  connectWsFunctional,
  empty_ws_state_functional,
  plain_object,
} from "django-rest-react";
import { APIErrorCatcher } from "react-router-utils";

import "@fontsource-variable/inter/wght.css";
import "./app.css";
import { useTranslation } from "react-i18next";
import { get_config, get_my_permissions, get_whoami, isApiRequestError } from "../api";
import { connectNotifications, onNotificationKind } from "../api/notifications";
import { subscribe } from "../api/webpush";
import {
  DEBUG,
  FRONTEND_STATIC_BASE_URL,
  IS_COMPANY_ROUTER_FRONTEND,
  IS_STATIC_DEMO_FRONTEND,
  SERVICE_WORKER_URL,
  TESTING,
  frontendCapabilities,
  login_url,
  sentry_config,
} from "../globals";
import initializeI18n from "../globals/i18n";
import { english } from "../globals/i18n/english";
import DashboardDateSelectionProvider from "./dashboard/DashboardDateSelectionProvider";
import DashboardPeopleSelectionProvider from "./dashboard/DashboardPeopleSelectionProvider";
import { peopleStorageKey } from "./dashboard/DashboardPeopleSelectionContext";
import LimitedFunctionalityBanner from "./dashboard/LimitedFunctionalityBanner";
import { dateStorageKey } from "./dashboard/dateSelection";
import { create_registration, reducers_map, useLogin, useToast } from "../reducers";
import { setConfig } from "../reducers/config";
import { type LoginState, set_user_data } from "../reducers/login";
import {
  Analytics,
  ChatInteractive,
  DemoUserImport,
  Downloads,
  ExecutiveDashboard,
  Overview,
  ProcessDetail,
  PromptConfig,
  Processes,
  RecordingImport,
  RecordingSession,
  Recordings,
  SectionDemos,
  Settings,
  UserManagement,
  UserPermissions,
  Vocabulary,
  Watchlist,
} from "../screens";
import store from "../store";
import { AskProvider, useAskSidebar } from "../chat/AskContext";
import { AskSidebar } from "./chat/AskSidebar";
import ChatPage from "../screens/ChatPage";
import SessionsLayout from "../screens/SessionsLayout";
import { sentry_log } from "../utils";
import { SettingsProvider } from "../utils/SettingsContext";
import { APP_SCROLL_ID } from "../utils/appScroll";
import { useSidebarCollapsed } from "../utils/useSidebarCollapsed";
import { TITLE_BAR_HEIGHT, onShellOverlay } from "../utils/desktopShell";
import { canSeeExecutiveDashboard } from "../utils/permissions";
import useRecordingControls, { RecordingControlsProvider } from "../utils/useRecordingControls";
import { schedule_alerts_refresh } from "../watchlist/api";
import { useWatchlistUnreadCount } from "../watchlist/hooks";
import { CapturePrivacyModal, DashboardSidebar, DashboardTopBar, WindowControls } from "./";
import { FeedbackProvider, useShareFeedback } from "./FeedbackDialog";
import Loadable from "./Loadable";
import LoginAgainToast from "./LoginAgainToast";
import RecordingReadyToasts from "./RecordingReadyToasts";
import UpdateChecks from "./UpdateChecks";
import { getDashboardSidebarWidth } from "./dashboard/DashboardSidebar";
import { ActionItemsProvider } from "./dashboard/ActionItemsContext";
// Loaded lazily and only mounted in the static demo build, so the tour engine
// and the journeys' content stay out of the real app's bundle.
const DemoLibrary = React.lazy(() => import("./tour/DemoLibrary"));

const LayoutTopBar = ({
  title,
  sidebarCollapsed,
  titleBarControls,
  onShowCapturePrivacy,
}: {
  title: string;
  sidebarCollapsed: boolean;
  titleBarControls: React.ReactNode;
  onShowCapturePrivacy: () => void;
}) => {
  const {
    recordingStatus,
    recordingElapsedSeconds,
    handleStartRecording,
    handlePauseRecording,
    handleStopRecording,
    recordingPermissionsRequired,
    recordingPermissionsRecommended,
    recordingPermissionsOpen,
    openRecordingPermissions,
    closeRecordingPermissions,
    handleRecordingPermissionsStatus,
  } = useRecordingControls();

  return (
    <DashboardTopBar
      title={title}
      sidebarCollapsed={sidebarCollapsed}
      titleBarControls={titleBarControls}
      onShowCapturePrivacy={onShowCapturePrivacy}
      recordingStatus={recordingStatus}
      elapsedSeconds={recordingElapsedSeconds}
      onStartRecording={handleStartRecording}
      onPauseRecording={handlePauseRecording}
      onStopRecording={handleStopRecording}
      recordingPermissionsRequired={recordingPermissionsRequired}
      recordingPermissionsRecommended={recordingPermissionsRecommended}
      recordingPermissionsOpen={recordingPermissionsOpen}
      onOpenRecordingPermissions={openRecordingPermissions}
      onCloseRecordingPermissions={closeRecordingPermissions}
      onRecordingPermissionsStatus={handleRecordingPermissionsStatus}
      recordingControlsAvailable={frontendCapabilities.localRecording}
    />
  );
};

/**
 * The two addresses the desktop shell can name for an overlay — APP_PAGE_PATH
 * in the shell's app-menu.ts. Spelled once, route-table style (relative,
 * trailing slash): the ROUTES entries and the ShellOverlays checks below must
 * stay on the same paths, and nothing else ties them together.
 */
const CAPTURE_PRIVACY_ROUTE = "settings/privacy/";
const RECORDING_PERMISSIONS_ROUTE = "settings/recording-permissions/";
const BROWSER_LOGIN_ROUTE = "/browser-login/";
const BROWSER_LOGIN_COMPLETE_ROUTE = "/browser-login-complete/";
const LOGIN_COMPLETE_BROWSER_LOGIN_ROUTE = "/login/complete-browser-login/";

const isPublicAuthRoute = (pathname: string) =>
  pathname === BROWSER_LOGIN_ROUTE ||
  pathname === BROWSER_LOGIN_ROUTE.slice(0, -1) ||
  pathname === BROWSER_LOGIN_COMPLETE_ROUTE ||
  pathname === BROWSER_LOGIN_COMPLETE_ROUTE.slice(0, -1);

/** Whether `pathname` names the given overlay route, with or without the slash. */
const isOverlayRoute = (pathname: string, route: string) =>
  pathname.startsWith(`/${route}`.replace(/\/$/, ""));

/**
 * The overlays the desktop shell can ask for, and the two ways it asks. Its
 * event raises one over the page the user is already on, which is what the
 * shell's menus want; its addresses are the fallback for when there is no app
 * on screen to raise anything over — a cold window, or one still loading —
 * and those land on settings with the overlay up.
 *
 * Nothing here opens unasked: both ways start from a shell action the user
 * just took.
 */
const ShellOverlays = ({ onShowCapturePrivacy }: { onShowCapturePrivacy: () => void }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { openRecordingPermissions } = useRecordingControls();

  /** Raises what the given address stands for, and says whether it stood for anything. */
  const raiseOverlayFor = useCallback(
    (pathname: string) => {
      if (!frontendCapabilities.localRecording) {
        return false;
      }
      if (isOverlayRoute(pathname, RECORDING_PERMISSIONS_ROUTE)) {
        openRecordingPermissions();
      } else if (isOverlayRoute(pathname, CAPTURE_PRIVACY_ROUTE)) {
        onShowCapturePrivacy();
      } else {
        return false;
      }
      return true;
    },
    [openRecordingPermissions, onShowCapturePrivacy],
  );

  useEffect(() => onShellOverlay(raiseOverlayFor), [raiseOverlayFor]);

  useEffect(() => {
    if (!raiseOverlayFor(location.pathname)) {
      return;
    }

    // Drop the address once it has been honoured: closing the modal then
    // leaves settings behind it instead of a route with nothing of its own to
    // show, and the next request for the same address is a fresh navigation.
    navigate("/settings/", { replace: true });
  }, [location.pathname, raiseOverlayFor]);

  return null;
};

const LayoutContent = ({ elem }: { elem: React.ReactNode }) => {
  const ask = useAskSidebar();
  const shareFeedback = useShareFeedback();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { permissions, user } = useLogin();
  const [personalActivity, setPersonalActivity] = useState(false);
  const dashboardDateStorageKey = dateStorageKey(user.id, user.company_id);
  const dashboardPeopleStorageKey = peopleStorageKey(user.id, user.company_id);
  // The detail page only — the process list at /processes/ keeps the sidebar.
  const isProcessDetail = /^\/processes\/./.test(location.pathname);
  const { sidebarCollapsed, toggleSidebarCollapsed } = useSidebarCollapsed(isProcessDetail);
  // Owned here, not by the sidebar: a modal rendered inside it is trapped in
  // its stacking context and paints under everything else.
  const [capturePrivacyOpen, setCapturePrivacyOpen] = useState(false);
  // Stable, so the shell listener it ends up in is not torn down and rebuilt
  // on every render of the layout.
  const showCapturePrivacy = useCallback(() => setCapturePrivacyOpen(true), []);
  const watchlistUnreadCount = useWatchlistUnreadCount();
  const authStateWs = useRef<WsStateFunctional>(empty_ws_state_functional);

  useEffect(() => {
    if (!frontendCapabilities.localRecording) {
      return;
    }

    const msg_handler = () => {
      window.location.reload();
    };
    connectWsFunctional(authStateWs.current, "/ws/auth-state/", msg_handler);

    return () => {
      cleanupWs(authStateWs.current);
    };
  }, []);

  const sidebarWidth = getDashboardSidebarWidth(sidebarCollapsed);
  // One instance, handed to whichever of the two owns the title-bar band.
  const titleBarControls = (
    <WindowControls collapsed={sidebarCollapsed} onToggleCollapse={toggleSidebarCollapsed} />
  );

  const normalizedPath = location.pathname.replace(/\/$/, "");
  useLayoutEffect(() => {
    if (["", "/processes", "/recordings", "/chat", "/watchlist"].includes(normalizedPath)) {
      const content = document.getElementById(APP_SCROLL_ID);
      if (content) content.scrollTop = 0;
    }
  }, [normalizedPath]);
  const overviewActive = normalizedPath === "";
  const chatActive = normalizedPath === "/chat" || location.pathname.startsWith("/chat/");
  const processesActive = location.pathname.startsWith("/processes");
  const activityLabel = t(personalActivity ? "activity.my_title" : "activity.team_title");
  const recordingsActive = location.pathname.startsWith("/recordings");
  const executiveDashboardActive = location.pathname.startsWith("/administration/");
  const downloadsActive = location.pathname.startsWith("/downloads/");
  // Only the pages about captured work: the landing pages and the sessions themselves.
  const showLimitedFunctionalityBanner =
    recordingsActive || ["", "/processes", "/watchlist"].includes(normalizedPath);
  const showExecutiveDashboard = canSeeExecutiveDashboard(permissions);
  // The bar is the window's title bar, so it is present on every route; a
  // route with no name for itself simply shows none.
  let topBarTitle = "";
  if (overviewActive) {
    topBarTitle = t("dashboard.overview");
  } else if (chatActive) {
    topBarTitle = t("dashboard.ask");
  } else if (normalizedPath === "/processes") {
    topBarTitle = t("processes");
  } else if (normalizedPath === "/recordings") {
    topBarTitle = activityLabel;
  } else if (location.pathname.startsWith("/section-demos")) {
    topBarTitle = t("tour.settings_title");
  } else if (normalizedPath === "/watchlist") {
    topBarTitle = t("watchlist.title");
  } else if (location.pathname.startsWith("/settings")) {
    topBarTitle = t("Settings");
  } else if (location.pathname.startsWith("/vocabulary")) {
    topBarTitle = t("vocabulary");
  } else if (location.pathname.startsWith("/recording-session")) {
    topBarTitle = t("recording_session");
  } else if (location.pathname.startsWith("/downloads")) {
    topBarTitle = t("downloads_page");
  } else if (location.pathname.startsWith("/administration/user-management")) {
    topBarTitle = t("user_management");
  } else if (location.pathname.startsWith("/administration/prompts")) {
    topBarTitle = t("prompt_config_title");
  } else if (executiveDashboardActive) {
    topBarTitle = t("executive_dashboard");
  }

  return (
    <div
      // The window itself never scrolls: the sidebar and the top bar are fixed
      // chrome, and the region between them is the one scroll container (see
      // APP_SCROLL_ID below). A document-level scrollbar would run the full
      // height of a window that has no browser chrome anywhere else.
      className="h-screen overflow-hidden bg-background text-text"
      style={{
        paddingLeft: sidebarWidth,
        ["--layout-sidebar-width" as string]: sidebarWidth,
      }}
    >
      <DashboardSidebar
        collapsed={sidebarCollapsed}
        titleBarControls={titleBarControls}
        onNavigateOverview={() => navigate("/")}
        overviewActive={overviewActive}
        onNavigateProcesses={() => navigate("/processes/")}
        processesActive={processesActive}
        onNavigateRecordings={() => navigate("/recordings/")}
        recordingsActive={recordingsActive}
        activityLabel={t(
          canBrowseTeamSessions(permissions.recordings.visualization, user.id)
            ? "activity.team_title"
            : "activity.my_title",
        )}
        onNavigateSettings={() => navigate("/settings/")}
        settingsActive={location.pathname.startsWith("/settings")}
        showDownloads={IS_COMPANY_ROUTER_FRONTEND || IS_STATIC_DEMO_FRONTEND}
        onNavigateDownloads={() => navigate("/downloads/")}
        downloadsActive={downloadsActive}
        showSectionDemos={IS_STATIC_DEMO_FRONTEND}
        onNavigateSectionDemos={() => navigate("/section-demos/")}
        sectionDemosActive={location.pathname.startsWith("/section-demos")}
        onNavigateVocabulary={() => navigate("/vocabulary/")}
        vocabularyActive={location.pathname.startsWith("/vocabulary")}
        showExecutiveDashboard={showExecutiveDashboard}
        onNavigateExecutiveDashboard={() => navigate("/administration/")}
        executiveDashboardActive={executiveDashboardActive}
        onNavigateWatchlist={() => navigate("/watchlist/")}
        watchlistActive={location.pathname.startsWith("/watchlist")}
        watchlistUnreadCount={watchlistUnreadCount}
        onShareFeedback={shareFeedback ?? undefined}
      />
      {/* Above the sidebar's z-30: this wrapper is a stacking context, so
          everything inside it is capped at this value however high a z-index
          it asks for. */}
      <div style={{ position: "relative", zIndex: 40 }}>
        <div style={{ position: "relative", zIndex: 1000 }}>
          <ToastFeed useToast={useToast} />
          <LoginAgainToast />
        </div>
        <div style={{ position: "relative", zIndex: 1 }}>
          <SettingsProvider>
            <RecordingControlsProvider>
              <ActionItemsProvider>
                <ShellOverlays onShowCapturePrivacy={showCapturePrivacy} />
                <UpdateChecks />
                <RecordingReadyToasts />
                <LayoutTopBar
                  title={topBarTitle}
                  sidebarCollapsed={sidebarCollapsed}
                  titleBarControls={titleBarControls}
                  onShowCapturePrivacy={showCapturePrivacy}
                />
                {frontendCapabilities.localRecording && (
                  <CapturePrivacyModal
                    open={capturePrivacyOpen}
                    onClose={() => setCapturePrivacyOpen(false)}
                  />
                )}
                {/* The app's one scroll container. It starts below the fixed
                    top bar, so the scrollbar belongs to the content rather
                    than to the window. */}
                <div
                  className="relative flex min-w-0 flex-col overflow-hidden md:flex-row"
                  style={{
                    marginTop: TITLE_BAR_HEIGHT,
                    height: `calc(100vh - ${TITLE_BAR_HEIGHT})`,
                  }}
                >
                  <main
                    id={APP_SCROLL_ID}
                    className={
                      ask.open && ask.expanded
                        ? "hidden"
                        : `app-scroll min-h-0 min-w-0 flex-1 overflow-auto ${ask.open ? "max-lg:invisible" : ""}`
                    }
                  >
                    {showLimitedFunctionalityBanner && <LimitedFunctionalityBanner />}
                    <DashboardDateSelectionProvider
                      key={dashboardDateStorageKey}
                      storageKey={dashboardDateStorageKey}
                    >
                      <DashboardPeopleSelectionProvider
                        key={dashboardPeopleStorageKey}
                        storageKey={dashboardPeopleStorageKey}
                      >
                        <ActivityPageContext.Provider value={setPersonalActivity}>
                          {elem}
                        </ActivityPageContext.Provider>
                      </DashboardPeopleSelectionProvider>
                    </DashboardDateSelectionProvider>
                  </main>
                  <AskSidebar />
                </div>
                {/* The demo journeys' guided tours (every piece portals to the
                  body; the mount here only provides Router context). */}
                {IS_STATIC_DEMO_FRONTEND && (
                  <React.Suspense fallback={null}>
                    <DemoLibrary />
                  </React.Suspense>
                )}
              </ActionItemsProvider>
            </RecordingControlsProvider>
          </SettingsProvider>
        </div>
      </div>
    </div>
  );
};

const LayoutFactory = (elem: React.ReactNode) => () => (
  <AskProvider>
    <FeedbackProvider>
      <LayoutContent elem={elem} />
    </FeedbackProvider>
  </AskProvider>
);

const Layout = LayoutFactory(<Outlet />);
const RouteError = () => {
  const { t } = useTranslation();
  usePageTitle(t("page_error"));
  return (
    <APIErrorCatcher
      get_username={(s: LoginState) => s.user.username}
      useLogin={useLogin}
      sentry_config={sentry_config}
    />
  );
};
const ErrorElement = LayoutFactory(<RouteError />);

/** A question's old watchlist address, forwarded to its chat one. */
const AskRedirect = () => {
  const { questionId } = useParams<{ questionId: string }>();
  return <Navigate to={`/chat/${questionId}/`} replace />;
};

/** Preserve existing bookmarks when moving the administration section. */
export const AdministrationRedirect = () => {
  const { pathname, search, hash, state } = useLocation();
  return (
    <Navigate
      to={{
        pathname: pathname.replace(/^\/executive-dashboard(?=\/|$)/, "/administration"),
        search,
        hash,
      }}
      state={state}
      replace
    />
  );
};

const NoMatch = () => {
  const { t } = useTranslation();
  const location = useLocation();
  usePageTitle(t("page_not_found"));
  return (
    <div className="container px-6 pb-6 pt-7">
      <h1 className="page-header">Error 404</h1>
      <p>
        {t("no_results_for")} <code>{location.pathname}</code>
      </p>
    </div>
  );
};

const PublicAuthPage = ({ children }: { children: React.ReactNode }) => (
  <main className="flex min-h-screen items-center justify-center bg-background px-6 py-12 text-text">
    <section className="w-full max-w-md rounded-lg border border-outline bg-surface p-8 shadow-lift">
      {children}
    </section>
  </main>
);

const BrowserLoginWait = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(t("browser_login_waiting_status"));

  useEffect(() => {
    document.title = t("browser_login_wait_title");
  }, [t]);

  useEffect(() => {
    const redirect = searchParams.get("redirect") || window.location.origin;
    const completeUrl = new URL(LOGIN_COMPLETE_BROWSER_LOGIN_ROUTE, window.location.origin);
    completeUrl.searchParams.set("redirect", redirect);

    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const socket = new WebSocket(`${protocol}//${window.location.host}/login/auth-state/`);
    socket.addEventListener("message", () => {
      setStatus(t("browser_login_complete_status"));
      window.location.assign(completeUrl.toString());
    });
    socket.addEventListener("close", () => setStatus(t("browser_login_waiting_status")));
    socket.addEventListener("error", () => setStatus(t("browser_login_waiting_status")));
    return () => socket.close();
  }, [searchParams, t]);

  return (
    <PublicAuthPage>
      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted">Platonic</p>
      <h1 className="mt-3 text-2xl font-semibold text-text">{t("browser_login_wait_title")}</h1>
      <p className="mt-3 leading-6 text-text-mid">{t("browser_login_wait_message")}</p>
      <p className="mt-5 text-sm font-semibold text-primary">{status}</p>
    </PublicAuthPage>
  );
};

const BrowserLoginComplete = () => {
  const { t } = useTranslation();
  useEffect(() => {
    document.title = t("browser_login_done_title");
  }, [t]);

  return (
    <PublicAuthPage>
      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted">Platonic</p>
      <h1 className="mt-3 text-2xl font-semibold text-text">{t("browser_login_done_title")}</h1>
      <p className="mt-3 leading-6 text-text-mid">{t("browser_login_done_message")}</p>
    </PublicAuthPage>
  );
};

export const ROUTES = (
  <>
    <Route path="browser-login/" element={<BrowserLoginWait />} />
    <Route path="browser-login-complete/" element={<BrowserLoginComplete />} />
    <Route path="/*" element={<Layout />} errorElement={<ErrorElement />}>
      <Route path="" element={<Overview />} />
      <Route path="chat/" element={<ChatPage />} />
      <Route path="chat/:questionId/" element={<ChatPage />} />
      <Route path="processes/" element={<Processes />} />
      <Route path="recordings/" element={<SessionsLayout />}>
        <Route index element={<Recordings />} />
        <Route path=":recordingId/" element={<Analytics />} />
      </Route>
      <Route path="downloads/" element={<Downloads />} />
      {/* The demo gallery belongs to the public static demo, not the product. */}
      {IS_STATIC_DEMO_FRONTEND && <Route path="section-demos/" element={<SectionDemos />} />}
      <Route path="settings/" element={<Settings />} />
      {/* The two addresses the desktop shell can name for an overlay. Each
          renders the page the overlay sits over; ShellOverlays raises the
          overlay and then hands the URL back to plain settings. */}
      <Route path={CAPTURE_PRIVACY_ROUTE} element={<Settings />} />
      <Route path={RECORDING_PERMISSIONS_ROUTE} element={<Settings />} />
      <Route path="watchlist/" element={<Watchlist />} />
      {/* The ask flow's old addresses, kept as forwards to the chat page. */}
      <Route path="watchlist/setup/" element={<Navigate to="/watchlist/" replace />} />
      <Route path="watchlist/ask/" element={<Navigate to="/chat/" replace />} />
      <Route path="watchlist/ask/:questionId/" element={<AskRedirect />} />
      <Route path="watchlist/:alertId/" element={<Watchlist />} />
      <Route path="administration/" element={<ExecutiveDashboard />} />
      <Route path="administration/user-management/" element={<UserManagement />} />
      <Route
        path="administration/user-management/:userId/permissions/"
        element={<UserPermissions />}
      />
      <Route path="administration/prompts/" element={<PromptConfig />} />
      <Route path="administration/demo-users/" element={<DemoUserImport />} />
      <Route path="administration/recording-import/" element={<RecordingImport />} />
      <Route path="administration/recording-import/:recordingId/" element={<RecordingImport />} />
      {/* Keep legacy URLs registered for direct links as well as client navigation. */}
      <Route path="executive-dashboard/" element={<AdministrationRedirect />} />
      <Route path="executive-dashboard/user-management/" element={<AdministrationRedirect />} />
      <Route
        path="executive-dashboard/user-management/:userId/permissions/"
        element={<AdministrationRedirect />}
      />
      <Route path="executive-dashboard/prompts/" element={<AdministrationRedirect />} />
      <Route path="executive-dashboard/demo-users/" element={<AdministrationRedirect />} />
      <Route path="executive-dashboard/recording-import/" element={<AdministrationRedirect />} />
      <Route
        path="executive-dashboard/recording-import/:recordingId/"
        element={<AdministrationRedirect />}
      />
      <Route path="recording-session/" element={<RecordingSession />} />
      <Route path="processes/:slug/" element={<ProcessDetail />} />
      <Route path="vocabulary/" element={<Vocabulary />} />
      <Route path="chat-interactive/" element={<ChatInteractive />} />
      <Route path="*" element={<NoMatch />} />
    </Route>
  </>
);

export const AppFactory = (creator: typeof createBrowserRouter, store_: typeof store) => () => {
  const router = creator(createRoutesFromElements(ROUTES));

  return (
    <Loadable load={carica} log_error={sentry_log}>
      <Provider store={store_}>
        <RouterProvider router={router} />
      </Provider>
    </Loadable>
  );
};
const App = AppFactory(createBrowserRouter, store);

export default App;

const wrapper = document.getElementById("app");
const username = document.getElementById("user")?.textContent;
const carica: () => Promise<boolean> = async () => {
  const get_connection_toast = (val: string) => {
    return {
      title: english.connection_to_ws_lost,
      message: english.already_trying_to_reconnect_please_wait,
      generating_object: val,
      store: store,
    };
  };
  configure_drr(
    reducers_map,
    store,
    DEBUG,
    val => delete_old_toasts(get_connection_toast(val)),
    val => add_toast_action(get_connection_toast(val)),
  );

  if (!IS_STATIC_DEMO_FRONTEND && isPublicAuthRoute(window.location.pathname)) {
    return false;
  }

  if (!IS_STATIC_DEMO_FRONTEND && username === "__not_logged_in") {
    login_url();
    // This is not dead code because window.location.assing will take a moment before actually working.
    return true;
  }

  let user: Awaited<ReturnType<typeof get_whoami>>;
  try {
    store.dispatch(setConfig(await get_config()));
    user = await get_whoami();
    const permissions_prom = await get_my_permissions();
    store.dispatch(set_user_data(user, permissions_prom));
  } catch (err) {
    if (!isApiRequestError(err) || err.status !== 401) {
      throw err;
    }
    // The page rendered as logged-in but a boot request says otherwise — a stale
    // session (e.g. the backend restarted), or the core thinks to be logged in
    // while the company router says no. Either way: log in again, instead of
    // dead-ending on the loading-error card.
    login_url();
    return true;
  }

  if (sentry_config.use_sentry) {
    const name = !!user.first_name ? `${user.first_name} ${user.last_name}` : user.username;
    Sentry.setUser({
      id: user.id,
      username: user.username,
      ...(!!user.email ? { email: user.email } : {}),
      fullName: name,
    });
  }

  if (IS_STATIC_DEMO_FRONTEND) {
    return false;
  }
  // In-app delivery always rides the websocket — no permission, no service
  // worker. A watchlist run landing refreshes the store for the sidebar dot.
  onNotificationKind("watchlist-run", schedule_alerts_refresh);
  connectNotifications();

  // Everything below is the web-push leg, dormant until VAPID returns.
  if (!frontendCapabilities.serviceWorkerPush) {
    return false;
  }

  try {
    Notification;
  } catch (error) {
    console.log("Skippate notifiche durante il Loading");
    return false;
  }

  if (!Notification || !navigator) {
    console.error("Notifications not supported in this browser");
    return false;
  }
  if (Notification.permission === "default") {
    // TODO these lines will be kept commented until
    // we restore the webpush functionality in the company router.
    // for now, requesting notification access is only bad user experience.
    // try {
    //   await Notification.requestPermission();
    // } catch (error) {
    //   if (error instanceof TypeError) {
    //     Notification.requestPermission(() => {});
    //   } else {
    //     throw error;
    //   }
    // }
  }

  if (!navigator.serviceWorker) {
    console.error("Service worker not supported, no push notifications available");
    return false;
  }
  const reg = await navigator.serviceWorker.register(SERVICE_WORKER_URL, {
    scope: FRONTEND_STATIC_BASE_URL,
  });
  store.dispatch(create_registration(reg));
  try {
    await subscribe(reg);
  } catch (err) {
    console.error(
      "Failed to register notification service worker, notifications will not work.",
      err,
    );
  }
  return false;
};

export const generate_sitemap = () => {
  const paths: string[] = [];
  const visit = (routes: RouteObject[], parentPath = "") => {
    for (const route of routes) {
      // The catch-all layout route wraps the app without adding a path segment.
      const own = route.path === "/*" ? undefined : route.path;
      const path = own ? `${parentPath}${own}` : parentPath;
      if (own) paths.push(path);
      if (route.children) visit(route.children, path ? path.replace(/\/?$/, "/") : "");
    }
  };
  // Nested detail pages also need backend routes for direct links and refreshes.
  visit(createRoutesFromElements(ROUTES));
  return paths;
};

if (sentry_config.use_sentry) {
  Sentry.init({
    dsn: sentry_config.dsn,
    release: sentry_config.release,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: false,
      }),
    ],
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

if (!TESTING && !!wrapper) {
  initializeI18n();
  createRoot(wrapper).render(<App />);
}
